import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Store, CheckCircle, XCircle, Clock, Loader2, MapPin, Phone, Mail,
  Calendar, Building2, Edit, Trash2,
} from 'lucide-react';
import { ClickableImage } from '../ImagePreview';
import { MerchantServicesManager } from '../MerchantServicesManager';
import { STATUS_STYLES, BUSINESS_TYPES } from './constants';

/**
 * Read-only detail view of one merchant application. Footer renders
 * approve/reject for pending merchants and edit/delete for approved ones.
 * All mutations delegate back to the coordinator.
 */
export function MerchantDetailDialog({
  open, onOpenChange, merchant, api, actionLoading,
  onApprove, onAskReject, onAskEdit, onAskDelete,
}) {
  if (!merchant) {
    return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="bg-[#041A3B] border-white/10 max-w-lg" /></Dialog>;
  }
  const info = merchant.merchant_info || {};
  const sty = STATUS_STYLES[merchant.merchant_status] || STATUS_STYLES.pending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Store className="w-5 h-5 text-[#00E676]" />
            {info.business_name || 'Merchant Application'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${sty.bg} ${sty.text} ${sty.border}`}>
            {merchant.merchant_status === 'pending' && <Clock className="w-3 h-3" />}
            {merchant.merchant_status === 'approved' && <CheckCircle className="w-3 h-3" />}
            {merchant.merchant_status === 'rejected' && <XCircle className="w-3 h-3" />}
            {sty.label}
          </div>

          <div className="p-3 rounded-lg bg-[#000B18] space-y-2">
            <p className="text-[#5C7599] text-[10px] uppercase tracking-wider font-medium">Owner Information</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-[#A0B4CC] flex items-center gap-1.5"><Mail className="w-3 h-3" /> {merchant.email}</span>
              <span className="text-[#A0B4CC] flex items-center gap-1.5"><Phone className="w-3 h-3" /> {merchant.mobile || 'N/A'}</span>
              <span className="text-white col-span-2">{merchant.name}</span>
            </div>
          </div>

          <div className="p-3 rounded-lg bg-[#000B18] space-y-2">
            <p className="text-[#5C7599] text-[10px] uppercase tracking-wider font-medium">Business Details</p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-[#00E676]" />
                <span className="text-white font-medium">{info.business_name}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Store className="w-3.5 h-3.5 text-[#5C7599]" />
                <span className="text-[#A0B4CC]">{BUSINESS_TYPES[info.business_type] || info.business_type || 'Not specified'}</span>
              </div>
              {info.business_address && (
                <div className="flex items-start gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-[#5C7599] mt-0.5" />
                  <span className="text-[#A0B4CC]">{info.business_address}</span>
                </div>
              )}
              {info.business_description && (
                <p className="text-[#A0B4CC] text-xs leading-relaxed mt-1 pl-5">{info.business_description}</p>
              )}
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-[#5C7599]" />
                <span className="text-[#5C7599] text-xs">Applied: {new Date(info.applied_at || merchant.created_at).toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {merchant.id_photo_url && (
              <div>
                <p className="text-[#5C7599] text-[10px] uppercase mb-1">Government ID</p>
                <ClickableImage src={merchant.id_photo_url} label={`ID Photo — ${merchant.name || ''}`} alt="ID" className="rounded-lg border border-white/10 w-full h-24 object-cover" />
              </div>
            )}
            {merchant.selfie_url && (
              <div>
                <p className="text-[#5C7599] text-[10px] uppercase mb-1">Selfie</p>
                <ClickableImage src={merchant.selfie_url} label={`Selfie — ${merchant.name || ''}`} alt="Selfie" className="rounded-lg border border-white/10 w-full h-24 object-cover" />
              </div>
            )}
            {info.business_permit_url && (
              <div className="col-span-2">
                <p className="text-[#5C7599] text-[10px] uppercase mb-1">Business Permit</p>
                <ClickableImage src={info.business_permit_url} label={`Business Permit — ${info.business_name || ''}`} alt="Permit" className="rounded-lg border border-[#00E676]/20 w-full h-32 object-cover" />
              </div>
            )}
          </div>

          {merchant.merchant_status === 'rejected' && info.rejection_reason && (
            <div className="p-3 rounded-lg bg-[#FF1744]/5 border border-[#FF1744]/20">
              <p className="text-[#FF1744] text-xs font-medium">Rejection Reason:</p>
              <p className="text-[#A0B4CC] text-sm mt-1">{info.rejection_reason}</p>
            </div>
          )}

          {merchant.merchant_status === 'approved' && (
            <div className="p-3 rounded-lg bg-[#000B18] border border-white/5">
              <MerchantServicesManager
                api={api}
                merchantId={merchant.id}
                urlPrefix="/api/admin/merchants"
                testIdPrefix="admin"
              />
            </div>
          )}
        </div>

        {merchant.merchant_status === 'pending' && (
          <DialogFooter className="mt-4 flex gap-2">
            <Button variant="ghost" onClick={onAskReject} className="text-[#FF1744] hover:bg-[#FF1744]/10" data-testid="reject-merchant-btn">
              <XCircle className="w-4 h-4 mr-1.5" /> Reject
            </Button>
            <Button onClick={() => onApprove(merchant.id)} disabled={actionLoading} className="bg-[#00E676] text-[#000B18] hover:bg-[#00E676]/90" data-testid="approve-merchant-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1.5" /> Approve</>}
            </Button>
          </DialogFooter>
        )}

        {merchant.merchant_status === 'approved' && (
          <DialogFooter className="mt-4 flex gap-2">
            <Button variant="ghost" onClick={onAskDelete} className="text-[#FF1744] hover:bg-[#FF1744]/10" data-testid="delete-merchant-btn">
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete
            </Button>
            <Button onClick={onAskEdit} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90" data-testid="edit-merchant-btn">
              <Edit className="w-4 h-4 mr-1.5" /> Edit
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
